import { useMemo, useState, useEffect } from 'react';
import { FieldRow } from '../../shared';
import type { VettingNote, VettingStatus } from '../../../../types/vetting';
import styles from './VettingSection.module.css';

export interface VettingData {
  program_vetting_status: string | null;
  vetting_notes: unknown;
  qc_flag_vetting: boolean | null;
  qc_flag_vetting_notes: string | null;
}

export interface VettingSectionProps {
  data: VettingData;
  editable: boolean;
  onChange?: (updates: Partial<VettingData>) => void;
  onStatusChange?: (status: VettingStatus) => Promise<boolean> | boolean | void;
  statusSaving?: boolean;
  currentStatus?: VettingStatus | null;
}

const parseVettingNotes = (raw: unknown): VettingNote[] => {
  if (!raw) {
    return [];
  }

  if (Array.isArray(raw)) {
    return raw.filter((note): note is VettingNote => typeof note === 'object' && note !== null);
  }

  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        return parsed.filter((note): note is VettingNote => typeof note === 'object' && note !== null);
      }
    } catch {
      return [];
    }
  }

  return [];
};

const formatTimestamp = (timestamp?: number): string => {
  if (!timestamp || Number.isNaN(timestamp)) {
    return 'No timestamp';
  }
  const date = new Date(timestamp * 1000);
  if (Number.isNaN(date.getTime())) {
    return 'Unknown date';
  }
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatActionType = (actionType: VettingNote['action_type'] | null | undefined): string => {
  if (!actionType || typeof actionType !== 'string') {
    return 'Unknown action';
  }
  return actionType.replace(/_/g, ' ');
};

const STATUS_OPTIONS: Array<{ value: VettingStatus; label: string }> = [
  { value: 'green', label: 'Approved (Green)' },
  { value: 'yellow', label: 'Review (Yellow)' },
  { value: 'red', label: 'Not Suitable (Red)' }
];

const formatStatusLabel = (status: string | null): string => {
  const match = STATUS_OPTIONS.find((option) => option.value === status);
  if (match) return match.label;
  return 'Not evaluated';
};

export function VettingSection({ data, editable, onChange, onStatusChange, statusSaving, currentStatus }: VettingSectionProps) {
  const [localData, setLocalData] = useState<VettingData>(data);

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const parsedNotes = useMemo(() => parseVettingNotes(localData.vetting_notes), [localData.vetting_notes]);

  const resolvedStatus = currentStatus ?? localData.program_vetting_status ?? null;

  const hasVettingRecord = Boolean(
    resolvedStatus !== null
      || localData.qc_flag_vetting !== null
      || parsedNotes.length > 0
  );

  const handleNotesChange = (value: string) => {
    const updated: VettingData = {
      ...localData,
      qc_flag_vetting_notes: value
    };
    setLocalData(updated);
    onChange?.({ qc_flag_vetting_notes: value });
  };

  const handleCheckboxToggle = (checked: boolean) => {
    const updated: VettingData = {
      ...localData,
      qc_flag_vetting: checked
    };
    setLocalData(updated);
    onChange?.({ qc_flag_vetting: checked });
  };

  const handleStatusChange = (next: VettingStatus) => {
    const previous = resolvedStatus;
    const updated: VettingData = {
      ...localData,
      program_vetting_status: next
    };
    setLocalData(updated);
    const maybeResult = onStatusChange?.(next);
    if (typeof maybeResult === 'boolean' && !maybeResult) {
      setLocalData((current) => ({
        ...current,
        program_vetting_status: previous
      }));
    } else if (maybeResult && typeof (maybeResult as Promise<boolean>).then === 'function') {
      void (maybeResult as Promise<boolean>).then((success) => {
        if (success === false) {
          setLocalData((current) => ({
            ...current,
            program_vetting_status: previous
          }));
        }
      }).catch(() => {
        setLocalData((current) => ({
          ...current,
          program_vetting_status: previous
        }));
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.summaryGrid}>
        <div className={styles.fieldControl}>
          <span className={styles.fieldLabel}>Vetting Status:</span>
          {editable && onStatusChange ? (
            <select
              className={styles.select}
              value={resolvedStatus ?? 'yellow'}
              onChange={(event) => handleStatusChange(event.target.value as VettingStatus)}
              disabled={statusSaving}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          ) : (
            <span className={styles.fieldValue}>{formatStatusLabel(resolvedStatus)}</span>
          )}
        </div>
        <div className={styles.fieldControl}>
          <span className={styles.fieldLabel}>QC Completed:</span>
          {editable ? (
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={!!localData.qc_flag_vetting}
                onChange={(event) => handleCheckboxToggle(event.target.checked)}
                disabled={!hasVettingRecord}
              />
              <span>{localData.qc_flag_vetting ? 'Completed' : 'Pending'}</span>
            </label>
          ) : (
            <span className={styles.fieldValue}>{localData.qc_flag_vetting ? 'Completed' : 'Pending'}</span>
          )}
          {!hasVettingRecord && editable && (
            <span className={styles.helperText}>Vetting record missing — complete upstream vetting before toggling.</span>
          )}
        </div>
      </div>

      <FieldRow
        label="QC Notes"
        value={localData.qc_flag_vetting_notes}
        multiline
        editable={editable}
        onChange={handleNotesChange}
      />

      <div className={styles.notesCard}>
        <h4>Vetting Activity Log</h4>
        {parsedNotes.length === 0 ? (
          <div className={styles.emptyState}>No vetting notes recorded yet.</div>
        ) : (
          parsedNotes.map((note, index) => (
            <div key={`${note.timestamp ?? 'note'}-${index}`} className={styles.noteEntry}>
              <div className={styles.noteMeta}>
                {formatTimestamp(note.timestamp)} · {formatActionType(note.action_type)}
              </div>
              {note.recommendation && <div>{note.recommendation}</div>}
              {note.user_action && <div>{note.user_action}</div>}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default VettingSection;
